import {ActivatedRoute, Router} from '@angular/router';

import {Observable, Subscription} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    PaymentMethodsEnum,
    PaymentProcessesEnum,
    PaymentStatusesEnum,
    AuthApiService,
    ReportsService,
    PaymentReportInterface,
    PaymentFilterModel,
    PaymentReportModel, PaymentPurchaseTypeEnum
} from '@spa/api';

import {Helper} from '../../../shared/classes';
import {PanelPaths} from '../../../shared/paths';
import {Pagination} from '../../../shared/components';
import {CacheCollectionService} from '../../../shared/services';
import {ItemsPerPageSelector} from '../../../shared/widgets/filter';
import {InitialComponentDataValidationException} from '../../../shared/exceptions';

import {EventsService} from '../services';
import {PaymentsReportResolverDataInterface} from '../interfaces';

import {ReportComponent} from './report-component';

export abstract class PaymentsReportComponent
    extends ReportComponent<PaymentReportInterface, PaymentReportModel> {

    private activatedRouteSubscription: Subscription;

    protected authApiService: AuthApiService;
    protected activatedRoute: ActivatedRoute;
    protected reportsService: ReportsService;

    public panelPaths: typeof PanelPaths;

    protected constructor(
        activatedRoute: ActivatedRoute,
        reportsService: ReportsService,
        router: Router,
        eventsService: EventsService,
        cacheCollectionService: CacheCollectionService,
        authApiService: AuthApiService
    ) {
        super(router, eventsService, cacheCollectionService);

        this.authApiService = authApiService;

        this.activatedRoute = activatedRoute;
        this.reportsService = reportsService;

        this.panelPaths = PanelPaths;
        this.filterModel = this.getInitialFilterModel();
    }

    onInit(): void {
        super.onInit();

        this.activatedRouteSubscription = this.activatedRoute.data
            .subscribe((result: { data: ApiResponse<ApiError[] | PaymentsReportResolverDataInterface> }) => {
                this.doInitialDataValidationCheck(result.data, () => {
                    this.filterControls = (result.data.data as PaymentsReportResolverDataInterface).filterControls;
                    this.dataTableConfig = (result.data.data as PaymentsReportResolverDataInterface).dataTableConfig;
                    this.modelsCollection = (result.data.data as PaymentsReportResolverDataInterface).modelsCollection;
                    this.filterModel = (result.data.data as PaymentsReportResolverDataInterface).filterModel;
                    const start = Pagination.calculateStartPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const end = Pagination.calculateEndPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const paginatedCollection = this.paginateCollection<PaymentReportModel>(this.modelsCollection, start, end);

                    this.paginationConfig.totalItems = this.modelsCollection.length;

                    this.dataTableConfig.rows = this.createDataSource(paginatedCollection);
                    this.dataTableConfig.headerChecked = false;
                    this.init();
                });
            });
    }

    init(): void{}

    onDestroy(): void {
        this.activatedRouteSubscription.unsubscribe();

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    getReportHandler(filter: PaymentFilterModel): Observable<ApiResponse<ApiError[] | PaymentReportInterface[]>> {
        return this.reportsService.getPaymentsReport(filter);
    }

    getFilterModel(data: { [p: string]: number | string | boolean | string[] }): PaymentFilterModel {
        const itemsPerPage = data[ItemsPerPageSelector] as number;
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk(itemsPerPage);

        const paymentStatus = data[PaymentFilterModel.getPaymentStatusSelector()] as number;
        const paymentProcess = data[PaymentFilterModel.getPaymentProcessSelector()] as number;
        const paymentPurchaseTypes = data[PaymentFilterModel.getPurchaseTypeIdsSelector()] as number;
        const paymentMethod = data[PaymentFilterModel.getPaymentMethodSelector()] as number;
        const currency = data[PaymentFilterModel.getCurrencySelector()] as string;

        const orderColumn = this.sortedColumn?.selector ?? PaymentReportModel.getDateSelector();
        const orderAscending = this.sortedColumn ? this.sortedColumn.ascending : false;

        return new PaymentFilterModel(
            null,
            (paymentStatus !== PaymentStatusesEnum.All) ? [paymentStatus] : null,
            (paymentProcess !== PaymentProcessesEnum.All) ? [paymentProcess] : null,
            (paymentMethod !== PaymentMethodsEnum.All) ? [paymentMethod] : null,
            data[PaymentFilterModel.getAccountOwnerNameSelector()] as string,
            data[PaymentFilterModel.getCreatedBySelector()] as string,
            null,
            data[PaymentFilterModel.getAccountReferenceIdSelector()] as string,
            null,
            null,
            null,
            (data[PaymentFilterModel.getAmountFromSelector()] as string)?.replace(/\s/g, ""),
            (data[PaymentFilterModel.getAmountToSelector()] as string)?.replace(/\s/g, ""),
            data[PaymentFilterModel.getDateFromSelector()] as string,
            data[PaymentFilterModel.getDateToSelector()] as string,
            data[PaymentFilterModel.getSettledDateFromSelector()] as string,
            data[PaymentFilterModel.getSettledDateToSelector()] as string,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            orderColumn,
            orderAscending,
            currency,
            data[PaymentFilterModel.getCategorySelector()] as string[],
            data[PaymentFilterModel.getTradingPlanReferenceIdSelector()] as string,
            (paymentPurchaseTypes !== PaymentPurchaseTypeEnum.All) ? [paymentPurchaseTypes] : null,
        );
    }

    mapFilterResults(results: PaymentReportInterface[]): PaymentReportModel[] {
        return results.map((report: PaymentReportInterface) => {
            const model = new PaymentReportModel(report);

            model.createDate = Helper.formatDate(model.createDate);
            model.settledDate = Helper.formatDate(model.settledDate);

            return model;
        });
    }

    validateInitialData(response: ApiResponse<ApiError[] | PaymentsReportResolverDataInterface>): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            throw new InitialComponentDataValidationException(response.messages);
        }
    }

    onRowClicked(paymentId: number | string | boolean): void {
        const filteredPayments = this.modelsCollection.filter((item: PaymentReportModel) => {
            return item.paymentId === paymentId;
        });

        if (filteredPayments.length !== 0) {
            this.router.navigate([
                this.panelPaths.root + '/' +
                this.panelPaths.children.payments.path + '/' +
                this.panelPaths.children.payments.children.edit.path + '/' +
                filteredPayments[0].paymentId
            ]).finally();
        }
    }


}
